/**
 * TanStack Query hooks для заказов.
 *
 * Замечание: бэкенд автоматически фильтрует список по роли текущего пользователя
 * (MEASURER → measurerId=me, INSTALLER → installerId=me, MANAGER → managerId=me).
 * Так что для замерщика/установщика дополнительно фильтровать на фронте не нужно —
 * просто запрашиваем /v1/orders.
 */
import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { OrderDetail, OrdersListResponse } from '@jaluzi/types';
import { api } from '../../lib/api.js';
import { db } from '../../lib/db/schema.js';
import { isServerUnreachable } from '../../lib/offline-unlock.js';
import { applyPendingOps, pendingOpsForOrder } from '../../lib/pending-order-overlay.js';
import { estimateOfflinePrice } from '../../lib/offline-price.js';
import { queryClient } from '../../lib/queryClient.js';
import { useAuth } from '../../stores/auth.js';
import { priceBookAccessoriesQuery, priceBookOfferQuery, type PbAccessory, type PbOffer } from '../pricebook/queries.js';

/**
 * Ответ сервера + копия в IndexedDB. Без сети (или пока сервер перезапускается)
 * отдаём последнюю копию: в отличие от кеша service worker она не истекает через
 * сутки и не зависит от того, есть ли SW (Telegram WebView, первая установка).
 */
async function fetchWithLocalCopy<T>(path: string, read: () => Promise<T | undefined>, write: (value: T) => Promise<unknown>): Promise<T> {
  try {
    const value = await api<T>(path);
    void write(value).catch(() => { /* копия — подстраховка, не мешаем онлайн-работе */ });
    return value;
  } catch (e) {
    if (!isServerUnreachable(e)) throw e;
    const local = await read().catch(() => undefined);
    if (local === undefined) throw e;
    return local;
  }
}

export interface UseOrdersListOptions {
  search?: string;
  status?: string;
  /** Только заказы, где этот сотрудник замерщик / монтажник (для ролей, которым сервер отдаёт все заказы). */
  measurerId?: string;
  installerId?: string;
}

/**
 * Один список на все разделы (Сегодня, Неделя, Касса, Сводка, Архив). Раньше
 * у каждого раздела был свой limit — это разные ключи кеша, и одни и те же
 * заказы грузились 3–5 раз при каждом возврате в приложение.
 */
export const ORDERS_LIST_LIMIT = 500;

/**
 * Заказы «Новый» и черновики из amoCRM ещё никому не назначены — с ними работают
 * в админке. Сотрудникам в мобильном показываем заказы начиная с «Назначен замер».
 * Клиент свой заказ видит с самого начала.
 */
export const FIELD_HIDDEN_STATUSES = ['NEW', 'DRAFT'] as const;

export function ordersListQueryOptions(opts: UseOrdersListOptions = {}) {
  const search = opts.search ?? '';
  const status = opts.status ?? '';
  const measurerId = opts.measurerId ?? '';
  const installerId = opts.installerId ?? '';
  const hideUnassigned = useAuth.getState().user?.role !== 'CLIENT';
  return queryOptions({
    // Общий ключ без исполнителя — тот же, что прогревает Shell.
    queryKey: measurerId || installerId
      ? ['orders', 'list', { search, status, measurerId, installerId, hideUnassigned }]
      : ['orders', 'list', { search, status, hideUnassigned }],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (status) qs.set('status', status);
      if (hideUnassigned) qs.set('excludeStatuses', FIELD_HIDDEN_STATUSES.join(','));
      if (measurerId) qs.set('measurerId', measurerId);
      if (installerId) qs.set('installerId', installerId);
      qs.set('limit', String(ORDERS_LIST_LIMIT));
      const path = `/v1/orders?${qs.toString()}`;
      return fetchWithLocalCopy<OrdersListResponse>(path,
        async () => (await db.ordersList.get(path))?.items as OrdersListResponse | undefined,
        (value) => db.ordersList.put({ key: path, items: value, total: value.items?.length ?? 0, updatedAt: Date.now() }));
    },
    // Свежесть статусов: список обновляется при возврате на экран/в приложение
    // (не нужно перезаходить в раздел, чтобы увидеть смену статуса/отказ).
    // 15 с — чтобы прогрев в Shell и открытие раздела не грузили список дважды.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useOrdersList(opts: UseOrdersListOptions = {}): UseQueryResult<OrdersListResponse> {
  return useQuery(ordersListQueryOptions(opts));
}

export function orderDetailQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['order', id],
    queryFn: async () => {
      const order = await fetchWithLocalCopy<OrderDetail>(`/v1/orders/${id}`,
        async () => (await db.orders.get(id!))?.payload as OrderDetail | undefined,
        (value) => db.orders.put({ id: id!, payload: value, updatedAt: Date.now() }));
      // Поверх ответа сервера — то, что замерщик сделал без сети и ещё не отправлено.
      const offer = queryClient.getQueryData<PbOffer>(priceBookOfferQuery.queryKey);
      const accessories = queryClient.getQueryData<PbAccessory[]>(priceBookAccessoriesQuery.queryKey);
      return applyPendingOps(order, await pendingOpsForOrder(id!), {
        offer,
        // Цена изделия из очереди — та же офлайн-оценка, что в калькуляторе.
        estimate: (item, size) => estimateOfflinePrice(item, size, { offer, accessories, orderRate: order })?.priceTiyin ?? null,
      });
    },
    enabled: !!id,
    refetchInterval: query => query.state.data?.installationIssue ? 10_000 : false,
    // Карточка всегда показывает актуальный статус: перезапрос при каждом открытии
    // и при возврате на вкладку (внешние изменения статуса видны сразу).
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useOrderDetail(id: string | undefined): UseQueryResult<OrderDetail> {
  return useQuery(orderDetailQueryOptions(id));
}
